import * as React from 'react';

import { Variable } from '@restworld/utility-types';

/**
 * One row of an association picker: the association's logo beside its name.
 *
 * Shared by the profile edit form and the onboarding wizard so the two pickers
 * cannot drift apart. Built as a label rather than a whole picker because the
 * two hosts differ in everything else (react-hook-form wiring, layout, styling)
 * and only this part is genuinely common.
 *
 * Neither `CheckboxGroup` nor `WizardMultiSelect` can render this: both read
 * only `key`, `label` and `id` off a Variable and drop `icon`, and the logo is
 * the point. So both call sites drive the `Checkbox` primitive directly and pass
 * this as `title`.
 *
 * A plain `img` rather than `next/image`, matching AssociationBadge and Avatar:
 * the URL is data from the API. It is also not an `imageVariant` call, since
 * that helper is for user-uploaded S3 assets with `_sm`/`_md`/`_lg` variants and
 * this is an association's own logo served at one fixed size.
 *
 * An association with no logo yet, or a URL that fails to load, falls back to
 * the name on its own rather than leaving a broken image in the list.
 */
export const AssociationOptionLabel = ({ association }: { association: Variable }) => {
  const [iconFailed, setIconFailed] = React.useState(false);

  return (
    <span className="flex flex-row items-center gap-2">
      {association.icon && !iconFailed && (
        <img
          src={association.icon}
          alt=""
          aria-hidden
          className="h-6 w-6 object-contain"
          onError={() => setIconFailed(true)}
        />
      )}
      <span>{association.label}</span>
    </span>
  );
};

export default AssociationOptionLabel;
